/* eslint-disable react/require-default-props */


import React from 'react';
import { Popover, OverlayTrigger, OverlayTriggerProps } from 'react-bootstrap';
import ColorPalette from 'components/color-palette';
import { LecturePage, NLecturePage } from 'redux/schemas/models/lecture-page';
import { colorRegex } from 'styles/global_defaults/colors';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import useUserColors from 'shared/hooks/use-user-colors';

const DEFAULT_POPOVER_OFFSET = 10;
/**
 * definitions:
 * - color value: an string value that is stored in the state (e.g. redux) that
 *   can be updated through this component. We can have 0 or more color values
 *   and usually one color input per color value.
 *
 * - color input: a React component that is the primary way to update the color
 *   value through a text input. Has a button to the right to show the color palette
 *
 * - color palette: the color palette is a UI with a table of predefined colors,
 *     institution brand colors and recently used colors. With this UI we
 *     manipulate one color value at a time
 */

/**
 * properties for the color value to be manipulated
 */
interface ColorValue {
  value: string;
  onChange(newValue: string): void;
}

/**
 * defines the properties that a color input component need to interact with the
 * color value. Also to show the color palette
 */
export interface ColorInputProps extends ColorValue {
  onValueChange(): void;
  showPalette(): void;
}

/**
 * an key-value map of color values
 */
export type ColorValuesMap<T extends string | number | symbol = string> = Record<T, ColorValue>;

export type GetColorInputProps<colorValuesT> = (color: colorValuesT) => ColorInputProps;

/**
 * parameters for the content render prop of the color picker
 */
export interface renderColorPickerPopoverContentParams<ColorValuesMapT extends ColorValuesMap> {
  getColorInputProps: GetColorInputProps<keyof ColorValuesMapT>;
}


type OverlayTriggerInheritedOptionalProps = Partial<Pick<OverlayTriggerProps, 'placement'>>;
type OverlayTriggerInheritedProps = Required<Pick<OverlayTriggerProps, 'children' | 'onToggle' | 'show'>> & OverlayTriggerInheritedOptionalProps;
export interface ColorPickerPopoverProps<ColorValuesMapT extends ColorValuesMap> extends OverlayTriggerInheritedProps {
  offset?: number;

  currentLecture?: NLecturePage;

  /**
   * render prop for the initial popover screen, e.g. color inputs.
   */
  renderContent(props: renderColorPickerPopoverContentParams<ColorValuesMapT>): React.ReactNode;

  /**
   * object with the `{colorValueName: currentValue, ...}` format
   */
  colorValues: ColorValuesMapT;
}
/**
 * See the <Style1SectionContainer /> implementation for usage examples.
 */
/* @ngInject */
export default function ColorPickerPopover<ColorValuesMapT extends ColorValuesMap>(
  props: ColorPickerPopoverProps<ColorValuesMapT>,
) {
  const [paletteVisible, setPaletteVisible] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState<keyof ColorValuesMapT>(null);
  const { updateRecentlyUsedColorsIfNeeded } = useUserColors({ currentLecture: props.currentLecture });
  function showPaletteFor(currentValue: keyof ColorValuesMapT) {
    setPaletteVisible(true);
    setSelectedValue(currentValue);
  }

  function handleOnPaletteValueChange(color: string) {
    props.colorValues[selectedValue].onChange(color);
    setPaletteVisible(false);
  }

  function getColorInputProps(colorValue: keyof ColorValuesMapT): ColorInputProps {
    const currentValue = props.colorValues[colorValue];
    return {
      onChange: currentValue.onChange,
      value: currentValue.value,
      showPalette: () => {
        showPaletteFor(colorValue);
      },
      onValueChange: () => {
        // add color to the recently used colors lists only if
        // is a valid RGB hex color, e.g. #fff or #0f0f0f
        if (currentValue.value.match(colorRegex)) {
          updateRecentlyUsedColorsIfNeeded(currentValue.value);
        }
      },
    };
  }

  // close the color palette on popover open
  React.useEffect(() => {
    if (props.show) {
      setPaletteVisible(false);
    }
  }, [props.show]);

  const overlay = (
    <Popover id='color-picker'>
      {paletteVisible ? (
        <ColorPalette
          activeColor={props.colorValues[selectedValue].value}
          onChange={handleOnPaletteValueChange}
          currentLecture={props.currentLecture}
        />
      ) : (
        <div
          css={{
            padding: standardSpacing,
          }}
        >
          {props.renderContent({ getColorInputProps })}
        </div>
      )}
    </Popover>
  );

  return (
    <OverlayTrigger
      key={
        // force remount when the content of the popover changes to get the proper alignment
        paletteVisible.toString()
      }
      popperConfig={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: () => [0, props.offset ?? DEFAULT_POPOVER_OFFSET],
            },
          },
        ],
      }}
      show={props.show}
      trigger='click'
      placement={props.placement || 'top'}
      overlay={overlay}
      onToggle={props.onToggle}
      rootClose
    >
      {props.children}
    </OverlayTrigger>
  );
}
